import React, { createContext, useState, useContext, useCallback } from "react";

export const UploadProgressContext = createContext({
  uploadState: null,
  startUpload: () => {},
  updateUploadProgress: () => {},
  completeUpload: () => {},
  failUpload: () => {},
  minimizeUpload: () => {},
  maximizeUpload: () => {},
  clearUpload: () => {},
  isMinimized: false,
});

export const UploadProgressProvider = ({ children }) => {
  const [uploadState, setUploadState] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);

  const startUpload = useCallback((data) => {
    setUploadState({
      ...data,
      status: "uploading",
      progress: 0,
      startedAt: new Date().toISOString(),
    });
    setIsMinimized(false);
  }, []);

  const updateUploadProgress = useCallback((updates) => {
    setUploadState((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        ...updates,
        updatedAt: new Date().toISOString(),
      };
    });
  }, []);

  const completeUpload = useCallback((data = {}) => {
    setUploadState((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        ...data,
        status: "completed",
        progress: 100,
        completedAt: new Date().toISOString(),
      };
    });
  }, []);

  const failUpload = useCallback((errorMessage) => {
    setUploadState((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        status: "failed",
        errorMessage,
        failedAt: new Date().toISOString(),
      };
    });
  }, []);

  const minimizeUpload = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeUpload = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const clearUpload = useCallback(() => {
    setUploadState(null);
    setIsMinimized(false);
  }, []);

  const contextValue = {
    uploadState,
    startUpload,
    updateUploadProgress,
    completeUpload,
    failUpload,
    minimizeUpload,
    maximizeUpload,
    clearUpload,
    isMinimized,
  };

  return (
    <UploadProgressContext.Provider value={contextValue}>
      {children}
    </UploadProgressContext.Provider>
  );
};

export const useUploadProgress = () => {
  const context = useContext(UploadProgressContext);
  if (!context) {
    throw new Error("useUploadProgress must be used within UploadProgressProvider");
  }
  return context;
};
